import { Global, css } from '@emotion/react';

import * as React from 'react';

import { type ColorSchemePreference, getInitialColorTheme, getThemeFromUrl, setColorSchemePreference } from '../utils';

const ColorThemeContext = React.createContext<{
  colorTheme: string;
  onColorThemeChange: (colorTheme: string) => void;
}>({
  colorTheme: 'light',
  onColorThemeChange: () => {},
});

export const useColorTheme = () => {
  return React.useContext(ColorThemeContext);
};

const SyncHtmlAtribute = () => {
  const { colorTheme } = useColorTheme();
  React.useEffect(() => {
    document.documentElement.setAttribute('data-color-theme', colorTheme);
  }, [colorTheme]);
  return null;
};

export const ColorThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [colorTheme, setColorTheme] = React.useState<string>(getInitialColorTheme);

  React.useEffect(() => {
    const themeFromUrl = getThemeFromUrl();
    if (themeFromUrl) {
      setColorSchemePreference(themeFromUrl as ColorSchemePreference);
    }
  }, []);

  return (
    <ColorThemeContext.Provider value={{ colorTheme, onColorThemeChange: setColorTheme }}>
      <SyncHtmlAtribute />
      {/* TODO: clean up this mapping after https://coursera.atlassian.net/browse/CDS-1740 is done */}
      {/* Currently Figma tokens for darkens, lightens, and elevation are mapped incorrectly */}
      <Global
        styles={css`
          :root[data-color-theme] {
            --cds-color-darken-975: light-dark(rgb(15 17 20 / 98%), rgb(255 255 255 / 98%));
            --cds-color-darken-950: light-dark(rgb(15 17 20 / 95%), rgb(255 255 255 / 95%));
            --cds-color-darken-900: light-dark(rgb(15 17 20 / 90%), rgb(255 255 255 / 90%));
            --cds-color-darken-800: light-dark(rgb(15 17 20 / 80%), rgb(255 255 255 / 80%));
            --cds-color-darken-700: light-dark(rgb(15 17 20 / 70%), rgb(255 255 255 / 70%));
            --cds-color-darken-600: light-dark(rgb(15 17 20 / 60%), rgb(255 255 255 / 60%));
            --cds-color-darken-500: light-dark(rgb(15 17 20 / 50%), rgb(255 255 255 / 50%));
            --cds-color-darken-400: light-dark(rgb(15 17 20 / 40%), rgb(255 255 255 / 40%));
            --cds-color-darken-300: light-dark(rgb(15 17 20 / 30%), rgb(255 255 255 / 30%));
            --cds-color-darken-200: light-dark(rgb(15 17 20 / 20%), rgb(255 255 255 / 20%));
            --cds-color-darken-100: light-dark(rgb(15 17 20 / 10%), rgb(255 255 255 / 10%));
            --cds-color-darken-50: light-dark(rgb(15 17 20 / 5%), rgb(255 255 255 / 5%));
            --cds-color-darken-25: light-dark(rgb(15 17 20 / 3%), rgb(255 255 255 / 3%));
            --cds-color-darken-0: light-dark(rgb(15 17 20 / 0%), rgb(255 255 255 / 0%));
            --cds-color-lighten-975: light-dark(rgb(255 255 255 / 98%), rgb(15 17 20 / 98%));
            --cds-color-lighten-950: light-dark(rgb(255 255 255 / 95%), rgb(15 17 20 / 95%));
            --cds-color-lighten-900: light-dark(rgb(255 255 255 / 90%), rgb(15 17 20 / 90%));
            --cds-color-lighten-800: light-dark(rgb(255 255 255 / 80%), rgb(15 17 20 / 80%));
            --cds-color-lighten-700: light-dark(rgb(255 255 255 / 70%), rgb(15 17 20 / 70%));
            --cds-color-lighten-600: light-dark(rgb(255 255 255 / 60%), rgb(15 17 20 / 60%));
            --cds-color-lighten-500: light-dark(rgb(255 255 255 / 50%), rgb(15 17 20 / 50%));
            --cds-color-lighten-400: light-dark(rgb(255 255 255 / 40%), rgb(15 17 20 / 40%));
            --cds-color-lighten-300: light-dark(rgb(255 255 255 / 30%), rgb(15 17 20 / 30%));
            --cds-color-lighten-200: light-dark(rgb(255 255 255 / 20%), rgb(15 17 20 / 20%));
            --cds-color-lighten-100: light-dark(rgb(255 255 255 / 10%), rgb(15 17 20 / 10%));
            --cds-color-lighten-50: light-dark(rgb(255 255 255 / 5%), rgb(15 17 20 / 5%));
            --cds-color-lighten-25: light-dark(rgb(255 255 255 / 3%), rgb(15 17 20 / 3%));
            --cds-color-lighten-0: light-dark(rgb(255 255 255 / 0%), rgb(15 17 20 / 0%));
            --cds-elevation-level3: 0px 0px 4px 0px light-dark(rgb(15 17 20 / 10%), rgb(255 255 255 / 10%)),
              0px 6px 12px 4px light-dark(rgb(15 17 20 / 10%), rgb(255 255 255 / 10%));
            --cds-elevation-level2: 0px 0px 4px 0px light-dark(rgb(15 17 20 / 10%), rgb(255 255 255 / 10%)),
              0px 4px 12px 4px light-dark(rgb(15 17 20 / 10%), rgb(255 255 255 / 10%));
            --cds-elevation-level1: 0px 0px 4px 0px light-dark(rgb(15 17 20 / 10%), rgb(255 255 255 / 10%)),
              0px 2px 12px 2px light-dark(rgb(15 17 20 / 10%), rgb(255 255 255 / 10%));
          }
        `}
      />
      {children}
    </ColorThemeContext.Provider>
  );
};
